import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, merge, timer, Subscription } from 'rxjs';
import { switchMap, tap, startWith } from 'rxjs/operators';
// FIX: Use named imports for Firebase v9+ modular SDK functions.
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { FIREBASE_AUTH, FIRESTORE, FUNCTIONS, getFunctionName } from '../firebase.config';
import { UserProfile, UserRole } from '../models/user.model';
import { sanitizeData } from '../utils/firebase.utils';
import { getClientAccessState } from '../utils/client-access.util';
import { AppCacheService } from '../services/app-cache.service';
import { LOGIN_AUTH_ERROR_ACCOUNT_NOT_ENABLED, LOGIN_AUTH_ERROR_QUERY_PARAM } from './auth.constants';

function getRoleHomeRoute(role: UserRole): string {
  if (role === 'Admin') {
    return '/admin';
  }

  if (role === 'Client') {
    return '/client';
  }

  if (role === 'Borrower') {
    return '/borrower';
  }

  return '/login';
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly E2E_PROFILE_KEY = 'arris.e2e.userProfile';
  private zone = inject(NgZone);
  private router: Router = inject(Router);
  private cache = inject(AppCacheService);
  private idleSubscription: Subscription | null = null;
  private readonly IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

  userProfile = signal<UserProfile | null | undefined>(undefined);

  isLoading = computed(() => this.userProfile() === undefined);
  isAuthenticated = computed(() => !!this.userProfile());
  currentUserRole = computed(() => this.userProfile()?.role ?? null);
  clientAccessState = computed(() => getClientAccessState(this.userProfile() ?? null));
  hasVerifiedClientAccess = computed(() => {
    const profile = this.userProfile();
    return profile?.role === 'Client' ? this.clientAccessState().hasAccess : false;
  });

  constructor() {
    const e2eProfile = this.loadE2eProfile();
    if (e2eProfile) {
      this.zone.run(() => {
        this.userProfile.set(e2eProfile);
        this.startIdleTimer();
        const currentUrl = this.router.url;
        if (currentUrl === '/' || currentUrl.startsWith('/login')) {
          this.router.navigate([getRoleHomeRoute(e2eProfile.role)]);
        }
      });
      return;
    }

    // FIX: Call functions directly from import.
    onAuthStateChanged(FIREBASE_AUTH, async (user) => {
      if (user) {
        try {
          const profile = await this.cache.readThrough(`user-profile:${user.uid}`, async () => {
            const userDocRef = doc(FIRESTORE, `users/${user.uid}`);
            const userDoc = await getDoc(userDocRef);

            if (!userDoc.exists()) {
              throw new Error(`User profile not found in Firestore for UID: ${user.uid}. This should not happen.`);
            }

            return sanitizeData(userDoc.data()) as UserProfile;
          }, 5 * 60 * 1000);

          // Refresh the token so any recently changed custom claims are visible immediately.
          // This avoids stale auth state when admin access is granted via claims first and Firestore later.
          await user.getIdToken(true);

          const tokenResult = await user.getIdTokenResult();
          const hasAdminClaim = tokenResult.claims?.admin === true;
          const effectiveRole: UserRole = profile.role === 'Admin' || hasAdminClaim ? 'Admin' : profile.role;
          const effectiveProfile: UserProfile = effectiveRole === profile.role
            ? profile
            : { ...profile, role: effectiveRole };

          // Set default verification statuses if they don't exist
          if (effectiveProfile.role === 'Client') {
            if (!effectiveProfile.kycStatus) {
              effectiveProfile.kycStatus = 'Action Required';
            }
            if (!effectiveProfile.amlStatus) {
              effectiveProfile.amlStatus = 'Action Required';
            }
            if (!effectiveProfile.financialQuizStatus) {
              effectiveProfile.financialQuizStatus = 'Action Required';
            }
          }

          this.zone.run(() => {
            this.userProfile.set(effectiveProfile);
            this.startIdleTimer(); // Start the inactivity timer
            // Redirect after successful login if the user is on the login page.
            const currentUrl = this.router.url;
            if (effectiveProfile && (currentUrl === '/' || currentUrl.startsWith('/login'))) {
              const targetUrl = getRoleHomeRoute(effectiveProfile.role);
              this.router.navigate([targetUrl]);
            }
          });

        } catch (error) {
          console.error("Critical Auth Service Error: Failed to get user profile. The user might exist in Auth but not in Firestore. Signing out.", error);
          // FIX: Call functions directly from import.
          await signOut(FIREBASE_AUTH);
          this.cache.clear();
          this.zone.run(() => {
            this.userProfile.set(null);
            this.router.navigate(['/login'], {
              queryParams: {
                [LOGIN_AUTH_ERROR_QUERY_PARAM]: LOGIN_AUTH_ERROR_ACCOUNT_NOT_ENABLED,
              },
              replaceUrl: true,
            });
          });
        }
      } else {
        this.zone.run(() => {
          this.userProfile.set(null);
          this.stopIdleTimer(); // Stop the inactivity timer
        });
        this.cache.clear();
      }
    });
  }

  private loadE2eProfile(): UserProfile | null {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    const rawProfile = window.localStorage.getItem(AuthService.E2E_PROFILE_KEY);
    if (!rawProfile) {
      return null;
    }

    try {
      const profile = JSON.parse(rawProfile) as UserProfile;
      if (!profile?.uid || !profile?.role) {
        return null;
      }

      if (profile.role === 'Client') {
        profile.kycStatus ??= 'Action Required';
        profile.amlStatus ??= 'Action Required';
        profile.financialQuizStatus ??= 'Action Required';
      }

      return profile;
    } catch (error) {
      console.warn('Failed to parse E2E auth profile override.', error);
      return null;
    }
  }

  private startIdleTimer(): void {
    this.stopIdleTimer();

    const activityEvents$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'keypress'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'scroll')
    );

    this.idleSubscription = activityEvents$
      .pipe(
        startWith(null),
        switchMap(() => timer(this.IDLE_TIMEOUT_MS)),
        tap(() => {
          console.log('Session expired due to inactivity.');
          this.zone.run(() => this.logout());
        })
      )
      .subscribe();
  }

  private stopIdleTimer(): void {
    this.idleSubscription?.unsubscribe();
    this.idleSubscription = null;
  }

  async login(email: string, password: string): Promise<void> {
    // FIX: Call functions directly from import.
    await signInWithEmailAndPassword(FIREBASE_AUTH, email, password);
    // onAuthStateChanged will handle reading the profile and redirecting.
  }

  async logout(): Promise<void> {
    // FIX: Call functions directly from import.
    this.stopIdleTimer(); // Stop the inactivity timer
    await signOut(FIREBASE_AUTH);
    this.cache.clear();
    this.router.navigate(['/login']);
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    const { httpsCallable } = await import('firebase/functions');
    const sendPasswordResetEmail = httpsCallable(FUNCTIONS, getFunctionName('sendPasswordResetEmail'));
    await sendPasswordResetEmail({ email });
  }

  async verifyPasswordResetCode(code: string): Promise<string> {
    const { verifyPasswordResetCode } = await import('firebase/auth');
    return await verifyPasswordResetCode(FIREBASE_AUTH, code);
  }

  async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    const { confirmPasswordReset } = await import('firebase/auth');
    await confirmPasswordReset(FIREBASE_AUTH, code, newPassword);
  }
}
